import { WEB_APP_VERSION } from '../appVersion';

/** Path helpers — prod at /, Flux /dev behind Traefik strip. */
export function GetPathPrefix() {
  if (typeof window === 'undefined') return '';
  return window.location.pathname === '/dev' || window.location.pathname.startsWith('/dev/')
    ? '/dev'
    : '';
}

const WEB_APP_VERSION_HEADER = 'X-Riddler-Web-App-Version';
const WEB_APP_RELOAD_KEY = 'riddler_web_version_reload';

/**
 * If the API requires a newer SPA build, one-shot reload (sessionStorage guards loops).
 * Call on every API Response; safe when the header is missing (older API).
 */
export function MaybeForceClientRefresh(response) {
  if (typeof window === 'undefined' || !response?.headers?.get) return;
  const expected = (response.headers.get(WEB_APP_VERSION_HEADER) || '').trim();
  if (!expected) return;
  if (expected === WEB_APP_VERSION) {
    try {
      sessionStorage.removeItem(WEB_APP_RELOAD_KEY);
    } catch {
      /* ignore */
    }
    return;
  }
  let alreadyTried = null;
  try {
    alreadyTried = sessionStorage.getItem(WEB_APP_RELOAD_KEY);
  } catch {
    alreadyTried = null;
  }
  if (alreadyTried === expected) return;
  try {
    sessionStorage.setItem(WEB_APP_RELOAD_KEY, expected);
  } catch {
    /* ignore */
  }
  window.location.reload();
}

/** Local Parcel / loopback always counts as the unlocked-register "dev" client. */
export function IsLocalHost() {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
}

/** Dev client: localhost, Flux /dev prefix, or API health env=dev. */
export function IsDevClient(envName) {
  return IsLocalHost() || Boolean(GetPathPrefix()) || envName === 'dev';
}

export function GetApiBase() {
  return `${GetPathPrefix()}/api/v1`;
}

const AUTH_FAILURE_EVENT = 'riddler:auth-failure';

export function EmitAuthFailure(detail) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(AUTH_FAILURE_EVENT, { detail: detail || 'Not authenticated' }));
}

export function OnAuthFailure(handler) {
  if (typeof window === 'undefined') return () => {};
  const listener = (event) => handler(event?.detail);
  window.addEventListener(AUTH_FAILURE_EVENT, listener);
  return () => window.removeEventListener(AUTH_FAILURE_EVENT, listener);
}

function ReadToken() {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem('riddler_token');
}

function PersistToken(accessToken) {
  if (accessToken) localStorage.setItem('riddler_token', accessToken);
}

let refreshInFlight = null;

/** Slide JWT while the current bearer is still accepted. */
export async function RefreshAccessToken() {
  const token = ReadToken();
  if (!token) return null;
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const response = await fetch(`${GetApiBase()}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    MaybeForceClientRefresh(response);
    if (!response.ok) return null;
    const data = await response.json().catch(() => null);
    if (!data?.access_token) return null;
    PersistToken(data.access_token);
    return data;
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

export async function ApiFetch(path, options = {}) {
  const token = ReadToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${GetApiBase()}${path}`, {
    ...options,
    headers,
  });
  MaybeForceClientRefresh(response);

  let data = null;
  const text = await response.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { detail: text };
    }
  }

  if (response.status === 401) {
    const detail = typeof data?.detail === 'string' ? data.detail : 'Not authenticated';
    const isCredentialCall = (
      path.startsWith('/auth/login')
      || path.startsWith('/auth/register')
      || path.startsWith('/auth/allocate-username')
    );
    if (!isCredentialCall) {
      const canTryRefresh = Boolean(token) && !options._authRetry;
      if (canTryRefresh) {
        const refreshed = await RefreshAccessToken();
        if (refreshed?.access_token) {
          return ApiFetch(path, { ...options, _authRetry: true });
        }
      }
      EmitAuthFailure(detail);
    }
    throw new Error(detail);
  }

  if (!response.ok) {
    const detail = data?.detail;
    const message = typeof detail === 'string'
      ? detail
      : Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join(', ')
        : `Request failed (${response.status})`;
    throw new Error(message);
  }

  return data;
}

/**
 * POST SSE helper. Calls onEvent for each `data:` JSON payload.
 * Returns the final `done` event payload (or null).
 */
export async function ApiStream(path, { body, onEvent, _authRetry = false } = {}) {
  const token = ReadToken();
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${GetApiBase()}${path}`, {
    method: 'POST',
    headers,
    body: typeof body === 'string' ? body : JSON.stringify(body ?? {}),
  });
  MaybeForceClientRefresh(response);

  if (response.status === 401) {
    const detail = 'Not authenticated';
    const canTryRefresh = Boolean(token) && !_authRetry;
    if (canTryRefresh) {
      const refreshed = await RefreshAccessToken();
      if (refreshed?.access_token) {
        return ApiStream(path, { body, onEvent, _authRetry: true });
      }
    }
    EmitAuthFailure(detail);
    throw new Error(detail);
  }

  if (!response.ok) {
    let data = null;
    const text = await response.text();
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { detail: text };
      }
    }
    const detail = data?.detail;
    const message = typeof detail === 'string'
      ? detail
      : Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join(', ')
        : `Request failed (${response.status})`;
    throw new Error(message);
  }

  if (!response.body) {
    throw new Error('Stream unavailable');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let donePayload = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split('\n\n');
    buffer = chunks.pop() || '';
    chunks.forEach((chunk) => {
      const lines = chunk.split('\n');
      const dataLine = lines.find((line) => line.startsWith('data:'));
      if (!dataLine) return;
      const raw = dataLine.slice(5).trim();
      if (!raw || raw === '[DONE]') return;
      let event;
      try {
        event = JSON.parse(raw);
      } catch {
        return;
      }
      if (event?.type === 'done') {
        donePayload = event;
      }
      if (event?.type === 'error') {
        throw new Error(
          typeof event.detail === 'string' ? event.detail : 'Stream failed'
        );
      }
      if (typeof onEvent === 'function') {
        onEvent(event);
      }
    });
  }

  return donePayload;
}
